import React, { useState, useEffect, useRef } from "react";
import Loader from "react-js-loader";
import * as d3 from "d3";
import linesegment from "../../assets/line-segment.svg";
import hline from "../../assets/horizontal-line.png";
import vline from "../../assets/vertical-line.png";
import undoIcon from "../../assets/undo.png";
import circleIcon from "../../assets/circle.png";

const WorkSpaceTopLeft = ({
  historicalData,
  ticker,
  loading,
  sessionAnnotations,
  setSessionAnnotations,
  actionHistory,
  setActionHistory,
  deletes,
  setDeletes,
}) => {
  const [period, setPeriod] = useState("2m");
  const [filteredData, setFilteredData] = useState([]);
  const [isHorizontalMode, setIsHorizontalMode] = useState(false);
  const [isVerticalMode, setIsVerticalMode] = useState(false);
  const [isCustomLineMode, setIsCustomLineMode] = useState(false);
  const [isCircleMode, setIsCircleMode] = useState(false);
  const [horizontalLines, setHorizontalLines] = useState([]);
  const [verticalLines, setVerticalLines] = useState([]);
  const [customLines, setCustomLines] = useState([]);
  const [circles, setCircles] = useState([]);
  const [texts, setTexts] = useState([]);
  const [circleCenter, setCircleCenter] = useState(null);
  const [previewY, setPreviewY] = useState(null);
  const [previewX, setPreviewX] = useState(null);
  const [previewCircle, setPreviewCircle] = useState(null);
  const [customLineStart, setCustomLineStart] = useState(null);
  const [isTextMode, setIsTextMode] = useState(false);
  const [inputPosition, setInputPosition] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [antype, setAntype] = useState(null);
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);
  const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 });
  const svgRef = useRef();

  useEffect(() => {
    if (historicalData && historicalData.length > 0) {
      filterDataByPeriod();
    }
    // eslint-disable-next-line
  }, [period, historicalData]);

  useEffect(() => {
    const handleClickOutside = () => {
      if (selectedAnnotation && antype) {
        setSelectedAnnotation(null);
        setAntype(null);
      }
    };
    window.addEventListener("click", handleClickOutside);
    return () => window.removeEventListener("click", handleClickOutside);
  }, [selectedAnnotation, antype]);

  const handleDelete = () => {
    if (selectedAnnotation && antype) {
      let updatedAnnotations = {
        ...sessionAnnotations,
        [ticker]: { ...sessionAnnotations[ticker] },
      };
      let updatedActionHistory = { ...actionHistory };

      const removeFromActionHistory = (type, valueToRemove) => {
        if (!updatedActionHistory[ticker]) return;

        updatedActionHistory[ticker] = updatedActionHistory[ticker].filter(
          (entry) => {
            if (entry.type !== type) return true;

            // Handle comparison by type
            switch (type) {
              case "horizontal":
                return entry.value !== valueToRemove;
              case "vertical":
                return (
                  new Date(entry.value).getTime() !==
                  new Date(valueToRemove).getTime()
                );
              case "custom":
                return !(
                  new Date(entry.value.start.date).getTime() ===
                    new Date(valueToRemove.start.date).getTime() &&
                  new Date(entry.value.end.date).getTime() ===
                    new Date(valueToRemove.end.date).getTime() &&
                  entry.value.start.price === valueToRemove.start.price &&
                  entry.value.end.price === valueToRemove.end.price
                );
              case "circle":
                return !(
                  new Date(entry.value.date).getTime() ===
                    new Date(valueToRemove.date).getTime() &&
                  entry.value.price === valueToRemove.price &&
                  entry.value.radius === valueToRemove.radius
                );
              case "text":
                return !(
                  entry.value.x === valueToRemove.x &&
                  entry.value.y === valueToRemove.y &&
                  entry.value.text === valueToRemove.text &&
                  new Date(entry.value.date).getTime() ===
                    new Date(valueToRemove.date).getTime() &&
                  entry.value.price === valueToRemove.price
                );
              default:
                return true;
            }
          }
        );
      };

      if (antype === "cLine") {
        const updatedLines = customLines.filter(
          (line) =>
            !(
              line.start.date === selectedAnnotation.start.date &&
              line.end.date === selectedAnnotation.end.date &&
              line.start.price === selectedAnnotation.start.price &&
              line.end.price === selectedAnnotation.end.price
            )
        );
        setCustomLines(updatedLines);
        updatedAnnotations[ticker].cLines = updatedLines;
        removeFromActionHistory("custom", selectedAnnotation);
      }

      if (antype === "hLine") {
        const updatedLines = horizontalLines.filter(
          (line) => line !== selectedAnnotation
        );
        setHorizontalLines(updatedLines);
        updatedAnnotations[ticker].hLines = updatedLines;
        removeFromActionHistory("horizontal", selectedAnnotation);
      }

      if (antype === "vLine") {
        const updatedLines = verticalLines.filter(
          (date) => date !== selectedAnnotation
        );
        setVerticalLines(updatedLines);
        updatedAnnotations[ticker].vLines = updatedLines;
        removeFromActionHistory("vertical", selectedAnnotation);
      }

      if (antype === "circle") {
        const updatedCircles = circles.filter(
          (circle) =>
            !(
              circle.date === selectedAnnotation.date &&
              circle.radius === selectedAnnotation.radius &&
              circle.price === selectedAnnotation.price
            )
        );
        setCircles(updatedCircles);
        updatedAnnotations[ticker].circles = updatedCircles;
        removeFromActionHistory("circle", selectedAnnotation);
      }

      if (antype === "text") {
        const updatedTexts = texts.filter(
          (text) =>
            !(
              text.x === selectedAnnotation.x &&
              text.y === selectedAnnotation.y &&
              text.text === selectedAnnotation.text &&
              text.date === selectedAnnotation.date &&
              text.price === selectedAnnotation.price
            )
        );
        setTexts(updatedTexts);
        updatedAnnotations[ticker].texts = updatedTexts;
        removeFromActionHistory("text", selectedAnnotation);
      }

      setSessionAnnotations(updatedAnnotations);
      setActionHistory(updatedActionHistory);
      setSelectedAnnotation(null);
      setAntype(null);
      setDeletes(deletes + 1);
    }
  };

  useEffect(() => {
    if (ticker) {
      if (sessionAnnotations[ticker]) {
        setHorizontalLines(sessionAnnotations[ticker].hLines || []);
        setVerticalLines(
          (sessionAnnotations[ticker].vLines || []).map((d) => new Date(d)) // <-- parse date strings
        );
        setCustomLines(
          (sessionAnnotations[ticker].cLines || []).map((line) => ({
            start: {
              date: new Date(line.start.date), // parse start date
              price: line.start.price,
            },
            end: {
              date: new Date(line.end.date), // parse end date
              price: line.end.price,
            },
          }))
        );
        setCircles(
          (sessionAnnotations[ticker].circles || []).map((circle) => ({
            date: new Date(circle.date),
            price: circle.price,
            radius: circle.radius,
          }))
        );
        setTexts(
          (sessionAnnotations[ticker].texts || []).map((text) => ({
            x: text.x,
            y: text.y,
            date: new Date(text.date),
            price: text.price,
            text: text.text,
          }))
        );
      } else {
        setHorizontalLines([]);
        setVerticalLines([]);
        setCustomLines([]);
        setCircles([]);
        setTexts([]);
      }
    }
  }, [ticker, sessionAnnotations]);

  useEffect(() => {
    setPeriod("2m");
  }, [ticker]);

  const filterDataByPeriod = () => {
    const endDate = new Date(historicalData[historicalData.length - 1].date);
    let startDate;
    switch (period) {
      case "2m":
        startDate = new Date(endDate);
        startDate.setMonth(endDate.getMonth() - 2);
        break;
      case "3m":
        startDate = new Date(endDate);
        startDate.setMonth(endDate.getMonth() - 3);
        break;
      case "6m":
        startDate = new Date(endDate);
        startDate.setMonth(endDate.getMonth() - 6);
        break;
      case "1y":
        startDate = new Date(endDate);
        startDate.setFullYear(endDate.getFullYear() - 1);
        break;
      case "5y":
        startDate = new Date(endDate);
        startDate.setFullYear(endDate.getFullYear() - 5);
        break;
      case "max":
        startDate = new Date(historicalData[0].date);
        break;
      default:
        startDate = new Date(endDate);
    }

    const filtered = historicalData.filter((data) => {
      const dataDate = new Date(data.date);
      return dataDate >= startDate && dataDate <= endDate;
    });

    setFilteredData(filtered);
  };

  const handleUndo = () => {
    const tickerHistory = actionHistory[ticker] || [];
    if (tickerHistory.length === 0) return;

    const lastAction = tickerHistory[tickerHistory.length - 1];
    const newHistory = tickerHistory.slice(0, -1);

    const updatedAnnotations = { ...sessionAnnotations };
    const updatedTickerAnnots = { ...updatedAnnotations[ticker] };

    switch (lastAction.type) {
      case "horizontal":
        setHorizontalLines(horizontalLines.slice(0, -1));
        updatedTickerAnnots.hLines = updatedTickerAnnots.hLines.slice(0, -1);
        break;
      case "vertical":
        setVerticalLines(verticalLines.slice(0, -1));
        updatedTickerAnnots.vLines = updatedTickerAnnots.vLines.slice(0, -1);
        break;
      case "custom":
        setCustomLines(customLines.slice(0, -1));
        updatedTickerAnnots.cLines = updatedTickerAnnots.cLines.slice(0, -1);
        break;
      case "circle":
        setCircles(circles.slice(0, -1));
        updatedTickerAnnots.circles = updatedTickerAnnots.circles.slice(0, -1);
        break;
      case "text":
        setTexts(texts.slice(0, -1));
        updatedTickerAnnots.texts = updatedTickerAnnots.texts.slice(0, -1);
        break;
      default:
        break;
    }

    updatedAnnotations[ticker] = updatedTickerAnnots;
    setSessionAnnotations(updatedAnnotations);
    setActionHistory({
      ...actionHistory,
      [ticker]: newHistory,
    });
  };

  useEffect(() => {
    if (!loading && historicalData.length > 0) {
      const width = 1060;
      const height = 370;
      const margin = { top: 10, right: 50, bottom: 35, left: 30 };

      const parseDate = d3.timeParse("%Y-%m-%d");
      const data = filteredData.map((d) => ({ ...d, date: parseDate(d.date) }));
      const candlestickWidth =
        ((width - margin.left - margin.right) / data.length) * 0.6;
      const xScale = d3
        .scaleTime()
        .domain([d3.min(data, (d) => d.date), d3.max(data, (d) => d.date)])
        .range([margin.left + candlestickWidth / 2, width - margin.right]);
      const yScale = d3
        .scaleLinear()
        .domain([d3.min(data, (d) => d.low), d3.max(data, (d) => d.high)])
        .nice()
        .range([height - margin.bottom, margin.top]);

      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();
      svg.attr("width", width).attr("height", height).append("g");

      // Add X axis
      const xAxis = d3
        .axisBottom(xScale)
        .ticks(15)
        .tickFormat(d3.timeFormat("%b %d"))
        .tickSizeOuter(0);
      svg
        .append("g")
        .attr("transform", `translate(0,${height - margin.bottom})`)
        .call(xAxis)
        .selectAll("text")
        .style("text-anchor", "end")
        .attr("dx", "-0.8em")
        .attr("dy", "0.15em")
        .attr("transform", "rotate(-45)")
        .style("fill", "#ffffff");
      // Add Y axis
      svg
        .append("g")
        .attr("transform", `translate(${margin.left},0)`)
        .call(d3.axisLeft(yScale))
        .selectAll("text")
        .style("fill", "#ffffff");
      // Draw candlesticks
      svg
        .append("g")
        .selectAll("rect")
        .data(data)
        .join("rect")
        .attr("x", (d, i) => xScale(d.date) - candlestickWidth / 2)
        .attr("y", (d) => yScale(Math.max(d.open, d.close)))
        .attr("width", candlestickWidth)
        .attr("height", (d) => Math.abs(yScale(d.open) - yScale(d.close)))
        .attr("fill", (d) => (d.close > d.open ? "#4caf50" : "#f44336"));
      // Draw high-low lines
      svg
        .append("g")
        .selectAll("line")
        .data(data)
        .join("line")
        .attr("x1", (d, i) => xScale(d.date))
        .attr("x2", (d, i) => xScale(d.date))
        .attr("y1", (d) => yScale(d.high))
        .attr("y2", (d) => yScale(d.low))
        .attr("stroke", (d) => (d.close > d.open ? "#4caf50" : "#f44336"));

      const clipPathId = "chart-clip-path";
      svg
        .append("defs")
        .append("clipPath")
        .attr("id", clipPathId)
        .append("rect")
        .attr("x", margin.left)
        .attr("y", margin.top)
        .attr("width", width - margin.left - margin.right)
        .attr("height", height - margin.top - margin.bottom);
      // Draw horizontal lines
      svg
        .selectAll(".horizontal-line")
        .data(horizontalLines)
        .join("line")
        .attr("class", "horizontal-line")
        .attr("x1", margin.left)
        .attr("x2", width - margin.right)
        .attr("y1", (d) => yScale(d))
        .attr("y2", (d) => yScale(d))
        .attr("stroke", "#ff9800")
        .attr("stroke-width", 2)
        .style("cursor", "pointer")
        .on("click", function (event, d) {
          event.stopPropagation();
          setSelectedAnnotation(d);
          setAntype("hLine");
          const [mouseX, mouseY] = d3.pointer(event);
          setContextMenuPos({ x: mouseX, y: mouseY });
        });
      // Draw vertical lines
      svg
        .selectAll(".vertical-line")
        .data(verticalLines)
        .join("line")
        .attr("class", "vertical-line")
        .attr("x1", (d) => xScale(d))
        .attr("x2", (d) => xScale(d))
        .attr("y1", margin.top)
        .attr("y2", height - margin.bottom)
        .attr("stroke", "#ff9800")
        .attr("stroke-width", 2)
        .style("cursor", "pointer")
        .on("click", function (event, d) {
          event.stopPropagation();
          setSelectedAnnotation(d);
          setAntype("vLine");
          const [mouseX, mouseY] = d3.pointer(event);
          setContextMenuPos({ x: mouseX, y: mouseY });
        });
      // Draw custom lines
      svg
        .selectAll(".custom-line")
        .data(customLines)
        .join("line")
        .attr("class", "custom-line")
        .attr("x1", (d) => xScale(d.start.date))
        .attr("x2", (d) => xScale(d.end.date))
        .attr("y1", (d) => yScale(d.start.price))
        .attr("y2", (d) => yScale(d.end.price))
        .attr("stroke", "#ff9800")
        .attr("stroke-width", 2)
        .attr("clip-path", `url(#${clipPathId})`)
        .style("cursor", "pointer")
        .on("click", function (event, d) {
          event.stopPropagation();
          setSelectedAnnotation(d);
          setAntype("cLine");
          const [mouseX, mouseY] = d3.pointer(event);
          setContextMenuPos({ x: mouseX, y: mouseY });
        });
      // Draw circles
      svg
        .selectAll(".circle")
        .data(circles)
        .join("circle")
        .attr("class", "circle")
        .attr("cx", (d) => xScale(d.date))
        .attr("cy", (d) => yScale(d.price))
        .attr("r", (d) => d.radius)
        .attr("fill", "none")
        .attr("stroke", "#ff9800")
        .attr("stroke-width", 2)
        .attr("clip-path", `url(#${clipPathId})`)
        .style("cursor", "pointer")
        .on("click", function (event, d) {
          event.stopPropagation();
          setSelectedAnnotation(d);
          setAntype("circle");
          const [mouseX, mouseY] = d3.pointer(event);
          setContextMenuPos({ x: mouseX, y: mouseY });
        });
      // Add texts
      svg
        .selectAll(".text-annotation")
        .data(texts)
        .join("text")
        .attr("class", "text-annotation")
        .attr("x", (d) => xScale(d.date))
        .attr("y", (d) => yScale(d.price) - 5)
        .attr("fill", "rgb(0, 238, 255)")
        .attr("font-size", "17px")
        .attr("text-anchor", "middle")
        .text((d) => d.text)
        .attr("clip-path", `url(#${clipPathId})`)
        .style("cursor", "pointer")
        .on("click", function (event, d) {
          event.stopPropagation();
          setSelectedAnnotation(d);
          setAntype("text");
          const [mouseX, mouseY] = d3.pointer(event);
          setContextMenuPos({ x: mouseX, y: mouseY });
        });

      if (previewCircle) {
        svg
          .append("circle")
          .attr("cx", xScale(previewCircle.center.date))
          .attr("cy", yScale(previewCircle.center.price))
          .attr("r", previewCircle.radius)
          .attr("fill", "none")
          .attr("stroke", "#ff9800")
          .attr("stroke-dasharray", "4")
          .attr("stroke-width", 1);
      }

      // Draw preview lines
      if (
        isHorizontalMode ||
        isVerticalMode ||
        isCustomLineMode ||
        isCircleMode
      ) {
        // Horizontal preview line
        if (isHorizontalMode && previewY !== null) {
          svg
            .append("line")
            .attr("class", "preview-line")
            .attr("x1", margin.left)
            .attr("x2", width - margin.right)
            .attr("y1", previewY)
            .attr("y2", previewY)
            .attr("stroke", "#ff9800")
            .attr("stroke-dasharray", "4")
            .attr("stroke-width", 1);
          // Show Y-axis value
          svg
            .append("text")
            .attr("class", "preview-label")
            .attr("x", margin.left + 10)
            .attr("y", previewY - 10)
            .attr("fill", "#ff9800")
            .attr("font-size", "12px")
            .text(`$${yScale.invert(previewY).toFixed(2)}`);
        }

        if (isVerticalMode && previewX !== null) {
          svg
            .append("line")
            .attr("class", "preview-line")
            .attr("x1", previewX)
            .attr("x2", previewX)
            .attr("y1", margin.top)
            .attr("y2", height - margin.bottom)
            .attr("stroke", "#ff9800")
            .attr("stroke-dasharray", "4")
            .attr("stroke-width", 1);
          // Show X-axis value
          svg
            .append("text")
            .attr("class", "preview-label")
            .attr("x", previewX + 10)
            .attr("y", height - margin.bottom + 20)
            .attr("fill", "#ff9800")
            .attr("font-size", "12px")
            .text(d3.timeFormat("%b %d, %Y")(xScale.invert(previewX)));
        }

        // Custom line preview
        if (isCustomLineMode && customLineStart) {
          // Draw preview line from start point to mouse cursor
          svg
            .append("line")
            .attr("class", "preview-line")
            .attr("x1", xScale(customLineStart.date))
            .attr("x2", previewX || xScale(customLineStart.date))
            .attr("y1", yScale(customLineStart.price))
            .attr("y2", previewY || yScale(customLineStart.price))
            .attr("stroke", "#ff9800")
            .attr("stroke-dasharray", "4")
            .attr("stroke-width", 1);
        }
      }

      // Handle mouse move events for preview lines
      svg.on("mousemove", function (event) {
        const [mouseX, mouseY] = d3.pointer(event);
        if (isHorizontalMode) {
          setPreviewY(mouseY);
        }
        if (isVerticalMode) {
          setPreviewX(mouseX);
        }
        if (isCustomLineMode) {
          setPreviewX(mouseX);
          setPreviewY(mouseY);
        }
        if (isCircleMode && circleCenter) {
          const radius = Math.sqrt(
            (mouseX - xScale(circleCenter.date)) ** 2 +
              (mouseY - yScale(circleCenter.price)) ** 2
          );
          setPreviewCircle({ center: circleCenter, radius });
        }
      });

      // Handle click events for placing lines
      svg.on("click", function (event) {
        const [mouseX, mouseY] = d3.pointer(event);

        if (isHorizontalMode) {
          const price = yScale.invert(mouseY).toFixed(2);
          setHorizontalLines([...horizontalLines, price]);
          const existingHistory = actionHistory[ticker] || [];
          setActionHistory({
            ...actionHistory,
            [ticker]: [
              ...existingHistory,
              { type: "horizontal", value: price },
            ],
          });
          const existing = sessionAnnotations[ticker] || {
            hLines: [],
            cLines: [],
            vLines: [],
            circles: [],
            notes: [],
            texts: [],
          };
          const updated = {
            ...sessionAnnotations,
            [ticker]: {
              ...existing,
              hLines: [...existing.hLines, price],
            },
          };
          setSessionAnnotations(updated);
          setIsHorizontalMode(false);
          setPreviewY(null);
          document.body.style.cursor = "default";
        }

        if (isVerticalMode) {
          const date = xScale.invert(mouseX);
          setVerticalLines([...verticalLines, date]);
          const existingHistory = actionHistory[ticker] || [];
          setActionHistory({
            ...actionHistory,
            [ticker]: [...existingHistory, { type: "vertical", value: date }],
          });
          const existing = sessionAnnotations[ticker] || {
            hLines: [],
            cLines: [],
            vLines: [],
            circles: [],
            notes: [],
            texts: [],
          };
          const updated = {
            ...sessionAnnotations,
            [ticker]: {
              ...existing,
              vLines: [...existing.vLines, date],
            },
          };
          setSessionAnnotations(updated);
          setIsVerticalMode(false);
          setPreviewX(null);
          document.body.style.cursor = "default";
        }

        if (isCustomLineMode) {
          if (!customLineStart) {
            setCustomLineStart({
              date: xScale.invert(mouseX),
              price: yScale.invert(mouseY).toFixed(2),
            });
          } else {
            const endPoint = {
              date: xScale.invert(mouseX),
              price: yScale.invert(mouseY).toFixed(2),
            };
            const newCustomLine = { start: customLineStart, end: endPoint };
            setCustomLines([...customLines, newCustomLine]);
            const existingHistory = actionHistory[ticker] || [];
            setActionHistory({
              ...actionHistory,
              [ticker]: [
                ...existingHistory,
                { type: "custom", value: newCustomLine },
              ],
            });
            const existing = sessionAnnotations[ticker] || {
              hLines: [],
              cLines: [],
              vLines: [],
              circles: [],
              notes: [],
              texts: [],
            };
            const updated = {
              ...sessionAnnotations,
              [ticker]: {
                ...existing,
                cLines: [...existing.cLines, newCustomLine],
              },
            };
            setSessionAnnotations(updated);
            setCustomLineStart(null);
            setIsCustomLineMode(false);
            setPreviewX(null);
            setPreviewY(null);
            document.body.style.cursor = "default";
          }
        }

        if (isCircleMode) {
          if (!circleCenter) {
            const date = xScale.invert(mouseX);
            const price = yScale.invert(mouseY).toFixed(2);
            setCircleCenter({ date, price });
          } else {
            const radius = Math.sqrt(
              (mouseX - xScale(circleCenter.date)) ** 2 +
                (mouseY - yScale(circleCenter.price)) ** 2
            ).toFixed(2);
            const newCircle = {
              date: circleCenter.date,
              price: circleCenter.price,
              radius,
            };

            setCircles([...circles, newCircle]);

            const existingHistory = actionHistory[ticker] || [];
            setActionHistory({
              ...actionHistory,
              [ticker]: [
                ...existingHistory,
                { type: "circle", value: newCircle },
              ],
            });

            const existing = sessionAnnotations[ticker] || {
              hLines: [],
              cLines: [],
              vLines: [],
              circles: [],
              notes: [],
              texts: [],
            };
            const updated = {
              ...sessionAnnotations,
              [ticker]: {
                ...existing,
                circles: [...existing.circles, newCircle],
              },
            };
            setSessionAnnotations(updated);
            setCircleCenter(null);
            setPreviewCircle(null);
            setIsCircleMode(false);
            document.body.style.cursor = "default";
          }
        }

        if (isTextMode) {
          const date = xScale.invert(mouseX);
          const price = yScale.invert(mouseY).toFixed(2);

          setInputPosition({ x: mouseX, y: mouseY, date, price });
          setInputValue(""); // Reset input value
          setIsTextMode(false);
          document.body.style.cursor = "default";
        }
      });

      // Clear preview lines when mouse leaves the chart
      svg.on("mouseleave", function () {
        setPreviewY(null);
        setPreviewX(null);
      });
    }
  }, [
    loading,
    ticker,
    historicalData,
    texts,
    antype,
    isTextMode,
    circleCenter,
    previewCircle,
    isCircleMode,
    circles,
    filteredData,
    horizontalLines,
    setSessionAnnotations,
    sessionAnnotations,
    customLines,
    verticalLines,
    isHorizontalMode,
    isCustomLineMode,
    isVerticalMode,
    previewY,
    previewX,
    customLineStart,
    actionHistory,
    setActionHistory,
  ]);

  return (
    <div
      className="workspace-top-left bg-dark text-white rounded flex-grow-1 mr-2 mb-1 p-3"
      style={{ width: "75%", position: "relative" }}
    >
      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center flex-column"
          style={{ height: "100%" }}
        >
          <Loader type="spinner-default" bgColor={"#FFFFFF"} size={100} />
          <div style={{ marginTop: 20, textAlign: "center", color: "#fff" }}>
            <span style={{ fontSize: "18px", fontWeight: "500" }}>
              Fetching Stock Details
            </span>
            <span className="dot-animation">
              <span>.</span>
              <span>.</span>
              <span>.</span>
            </span>
          </div>
        </div>
      ) : historicalData.length === 0 ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "100%" }}
        >
          <p>Select a ticker to view data</p>
        </div>
      ) : (
        <>
          <div className="tic-details">{ticker}</div>
          <div className="m-0 p-0 d-flex justify-content-between">
            <div className="opts1work">
              {["2m", "3m", "6m", "1y", "5y", "max"].map((option, index) => (
                <span
                  key={option}
                  onClick={() => setPeriod(option)}
                  style={{
                    backgroundColor: period === option ? "#ff9800" : "#333",
                    color: period === option ? "#000" : "#fff",
                  }}
                >
                  {option}
                </span>
              ))}
            </div>
            <div className="opts1work">
              <span
                onClick={() => {
                  setIsHorizontalMode(true);
                  setIsCustomLineMode(false);
                  setIsVerticalMode(false);
                  setIsCircleMode(false);
                  setIsTextMode(false);
                  document.body.style.cursor = "pointer";
                }}
                style={{
                  backgroundColor: isHorizontalMode ? "#ff9800" : "#333",
                }}
              >
                <img src={hline} alt="horizontal-line" width={20} height={20} />
              </span>
              <span
                onClick={() => {
                  setIsVerticalMode(true);
                  setIsHorizontalMode(false);
                  setIsCustomLineMode(false);
                  setIsCircleMode(false);
                  setIsTextMode(false);
                  document.body.style.cursor = "pointer";
                }}
                style={{ backgroundColor: isVerticalMode ? "#ff9800" : "#333" }}
              >
                <img src={vline} alt="vertical-line" width={20} height={20} />
              </span>
              <span
                onClick={() => {
                  setIsCustomLineMode(true);
                  setIsHorizontalMode(false);
                  setIsVerticalMode(false);
                  setIsCircleMode(false);
                  setIsTextMode(false);
                  document.body.style.cursor = "pointer";
                }}
                style={{
                  backgroundColor: isCustomLineMode ? "#ff9800" : "#333",
                }}
              >
                <img
                  src={linesegment}
                  alt="linesegment"
                  width={23}
                  height={23}
                />
              </span>
              <span
                onClick={() => {
                  setIsCircleMode(true);
                  setIsHorizontalMode(false);
                  setCircleCenter(null);
                  setIsVerticalMode(false);
                  setPreviewCircle(null);
                  setIsCustomLineMode(false);
                  setIsTextMode(false);
                  document.body.style.cursor = "pointer";
                }}
                style={{ backgroundColor: isCircleMode ? "#ff9800" : "#333" }}
              >
                <img src={circleIcon} alt="circle" width={20} height={20} />
              </span>
              <span
                onClick={() => {
                  setIsTextMode(true);
                  setIsHorizontalMode(false);
                  setIsVerticalMode(false);
                  setIsCustomLineMode(false);
                  setIsCircleMode(false);
                  document.body.style.cursor = "pointer";
                }}
                style={{ backgroundColor: isTextMode ? "#ff9800" : "#333" }}
              >
                Text
              </span>
              {inputPosition && (
                <input
                  type="text"
                  value={inputValue}
                  autoFocus
                  onChange={(e) => setInputValue(e.target.value)}
                  onBlur={() => {
                    if (inputValue.trim() !== "") {
                      const newText = { ...inputPosition, text: inputValue };
                      setTexts([...texts, newText]);
                      const existingHistory = actionHistory[ticker] || [];
                      setActionHistory({
                        ...actionHistory,
                        [ticker]: [
                          ...existingHistory,
                          { type: "text", value: newText },
                        ],
                      });
                      const existing = sessionAnnotations[ticker] || {
                        hLines: [],
                        cLines: [],
                        vLines: [],
                        circles: [],
                        texts: [],
                        notes: [],
                      };
                      const updated = {
                        ...sessionAnnotations,
                        [ticker]: {
                          ...existing,
                          texts: [...existing.texts, newText],
                        },
                      };
                      setSessionAnnotations(updated);
                    }
                    setInputPosition(null); // Hide input box after adding text
                    setIsTextMode(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      if (inputValue.trim() !== "") {
                        const newText = { ...inputPosition, text: inputValue };
                        setTexts([...texts, newText]);
                        const existingHistory = actionHistory[ticker] || [];
                        setActionHistory({
                          ...actionHistory,
                          [ticker]: [
                            ...existingHistory,
                            { type: "text", value: newText },
                          ],
                        });
                        const existing = sessionAnnotations[ticker] || {
                          hLines: [],
                          cLines: [],
                          vLines: [],
                          circles: [],
                          texts: [],
                          notes: [],
                        };
                        const updated = {
                          ...sessionAnnotations,
                          [ticker]: {
                            ...existing,
                            texts: [...existing.texts, newText],
                          },
                        };
                        setSessionAnnotations(updated);
                      }
                      setInputPosition(null);
                      setIsTextMode(false);
                    }
                  }}
                  style={{
                    left: `${inputPosition.x + 3}px`,
                    top: `${inputPosition.y + 17}px`,
                  }}
                />
              )}
              <span onClick={handleUndo} style={{ backgroundColor: "#333" }}>
                <img src={undoIcon} alt="undo" width={20} height={20} />
              </span>
            </div>
          </div>
          <svg ref={svgRef}></svg>
          {selectedAnnotation && (
            <div
              style={{ left: contextMenuPos.x, top: contextMenuPos.y }}
              className="del-can-buttons"
            >
              <button onClick={handleDelete} className="del-button">
                Delete
              </button>
              <button
                onClick={() => {
                  setSelectedAnnotation(null);
                  setAntype(null);
                }}
                className="can-button"
              >
                Cancel
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WorkSpaceTopLeft;
